import { apiSlice } from './apiSlice'

export interface RubricCriterion {
  key: string
  name: string
  description?: string
  max_score: number
  weight?: number
}

export interface Rubric {
  id?: string
  name: string
  description?: string
  criteria: RubricCriterion[]
  case_id?: string
  created_at?: string
  updated_at?: string
  created_by?: string
}

export interface RubricCreate {
  name: string
  description?: string
  criteria: RubricCriterion[]
  case_id?: string
}

export interface Score {
  id: string
  memorial_id?: string
  transcript_id?: string
  submission_type?: string // "memorial" or "transcript" or "evidence" or "intro_video"
  case_id: string
  round_id?: string
  team_id: string
  judge_id: string
  rubric_id?: string
  rubric_scores: Record<string, number>
  total_score: number
  weighted_score?: number
  comments?: string
  ai_suggestions?: Record<string, any>
  /** draft = chỉnh sửa được; submitted = đã gửi chính thức */
  status?: string | null
  submitted_at?: string | null
  scored_at?: string
  created_at?: string
  updated_at?: string
  // Spec PHẦN 4.1 — R1 dual_claimant_packets fields
  submission_index?: number | null  // 1 hoặc 2 (R1 sơ loại) — None với R2/R3
  representing_role?: string | null  // "plaintiff" | "defendant"
  evidence_score_breakdown?: Record<string, any> | null
  // Phiếu chấm Bán kết / Chung kết (BR-07-01 ext)
  scorecard_id?: string | null
  round_type?: string | null  // 'memorial' | 'semifinal' | 'final'
  courtroom_id?: string | null
  deadline_at?: string | null
  via_proxy?: boolean | null
  proxy_reason?: string | null
  delegating_judge_id?: string | null
  entered_by_admin_id?: string | null
  locked_at?: string | null
  lock_snapshot_hash?: string | null
  unlock_history?: Array<{ at: string; by: string; by_role?: string; reason?: string | null }> | null
  // Phase 1+ Match Context (2026-05-24) — link phiếu với match
  match_id?: string | null
  // Phase 1 immutability (2026-05-24) — set bởi publish workflow.
  // Khi có giá trị → phiếu bất biến vĩnh viễn: ScoringPage render readonly,
  // ẩn unlock/delete/Save button. Backend ScoringService._ensure_mutable
  // reject mọi update/delete attempt với ScoreImmutableError (400).
  published_at?: string | null
  published_in_result_id?: string | null
  version?: number | null
}

export interface ScoreCreate {
  memorial_id?: string
  transcript_id?: string
  submission_type?: string // "memorial" or "transcript"
  case_id: string
  team_id: string
  rubric_id?: string
  rubric_scores: Record<string, number>
  comments?: string
  ai_suggestions?: Record<string, any> | null
  // R1 dual_claimant_packets
  submission_index?: number
  representing_role?: string
  evidence_score_breakdown?: Record<string, any>
}

export interface ScoreUpdate {
  rubric_scores?: Record<string, number>
  comments?: string
  ai_suggestions?: Record<string, any>
  submission_index?: number
  representing_role?: string
  evidence_score_breakdown?: Record<string, any>
}

export interface AISuggestions {
  suggested_scores: Record<string, number>
  analysis: string
  recommendations: string[]
  confidence?: number
  // Reasoning ngắn per-criterion (chỉ có khi BE bật LLM_SCORING_ENABLED).
  per_criterion_reasoning?: Record<string, string>
}

export const scoringApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getScores: builder.query<Score[], { case_id?: string; team_id?: string; judge_id?: string }>({
      query: (params) => ({
        url: '/scoring/list',
        params,
      }),
      providesTags: ['Score'],
    }),
    getScore: builder.query<Score, string>({
      query: (id) => `/scoring/${id}`,
      providesTags: (result, error, id) => [{ type: 'Score', id }],
    }),
    createScore: builder.mutation<Score, ScoreCreate>({
      query: (score) => ({
        url: '/scoring/create',
        method: 'POST',
        body: score,
      }),
      invalidatesTags: ['Score'],
    }),
    updateScore: builder.mutation<Score, { id: string; update: ScoreUpdate }>({
      query: ({ id, update }) => ({
        url: `/scoring/${id}`,
        method: 'PUT',
        body: update,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Score', id }, 'Score'],
    }),
    deleteScore: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/scoring/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Score'],
    }),
    submitScore: builder.mutation<Score, string>({
      query: (id) => ({
        url: `/scoring/${id}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Score', id }, 'Score'],
    }),
    // 2026-05-25: JIT spawn phiếu Bán kết/Chung kết cho judge gọi —
    // fallback khi admin quên configure judges qua MatchConfigCard.
    ensureCourtroomScorecard: builder.mutation<
      { courtroom_id: string; created_count: number; created_ids: string[] },
      string
    >({
      query: (courtroomId) => ({
        url: `/scoring/courtroom/${courtroomId}/ensure-card`,
        method: 'POST',
      }),
      invalidatesTags: ['Score'],
    }),
    unlockScore: builder.mutation<Score, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/scoring/${id}/unlock`,
        method: 'POST',
        body: { reason: reason || null },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Score', id }, 'Score'],
    }),
    // "Yêu cầu chấm lại" — Admin từ Reports: reason BẮT BUỘC, gửi notify GK,
    // audit log action=score.reopen (phân biệt với unlock kỹ thuật).
    reopenScore: builder.mutation<Score, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/scoring/${id}/reopen`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Score', id }, 'Score'],
    }),
    // Spec PHẦN 4.1 — AI score evidence → chốt thành Score draft (admin review → lock).
    finalizeAiEvidenceScore: builder.mutation<
      Score,
      { case_id: string; team_id: string; submission_index: 1 | 2; representing_role?: string; rubric_id?: string }
    >({
      query: ({ case_id, team_id, submission_index, representing_role, rubric_id }) => ({
        url: `/scoring/ai-evidence-finalize/${case_id}/${team_id}/${submission_index}`,
        method: 'POST',
        params: {
          ...(representing_role ? { representing_role } : {}),
          ...(rubric_id ? { rubric_id } : {}),
        },
      }),
      invalidatesTags: ['Score'],
    }),
    getScorecardProgress: builder.query<
      Array<{
        courtroom_id: string | null
        judge_id: string
        round_type: 'semifinal' | 'final'
        case_id: string
        total: number
        submitted: number
        draft: number
        untouched: number
        deadline_at: string | null
        overdue: boolean
        via_proxy_count: number
        scorecard_ids: string[]
        any_via_proxy: boolean
      }>,
      { case_id?: string; courtroom_id?: string; round_type?: 'semifinal' | 'final' }
    >({
      query: (params) => ({
        url: '/scoring/scorecard-progress',
        params,
      }),
      providesTags: ['Score'],
    }),
    proxyEnterScore: builder.mutation<
      Score,
      {
        id: string
        rubric_scores: Record<string, number>
        comments?: string
        proxy_reason?: 'no_device' | 'tech_issue' | 'requested' | 'other'
        delegating_judge_id?: string
        submit?: boolean
      }
    >({
      query: ({ id, rubric_scores, comments, proxy_reason, delegating_judge_id, submit }) => ({
        url: `/scoring/${id}/proxy`,
        method: 'POST',
        body: {
          rubric_scores,
          comments: comments || null,
          proxy_reason: proxy_reason || 'other',
          delegating_judge_id: delegating_judge_id || null,
          submit: submit !== false,
        },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Score', id }, 'Score'],
    }),
    getAISuggestions: builder.query<AISuggestions, string>({
      query: (memorialId) => `/scoring/ai-suggestions/${memorialId}`,
    }),
    runAiPreliminaryMemorial: builder.mutation<
      {
        memorial_id: string
        case_id: string
        team_id: string
        excerpts: Record<string, unknown>[]
        governance_notice: string
      },
      string
    >({
      query: (memorialId) => ({
        url: `/scoring/ai-preliminary/memorial/${memorialId}`,
        method: 'POST',
      }),
    }),
    runAiEvidenceClassification: builder.mutation<
      {
        case_id: string
        team_id: string
        criterion_evidence_collection: {
          key: string
          max_score: number
          score: number
          picked_correct: number
          total_correct: number
          missed_correct: { document_id: string; title: string; file_name: string }[]
          picked_correct_items: { document_id: string; title: string; file_name: string }[]
          formula: string
        }
        criterion_evidence_filtering: {
          key: string
          max_score: number
          score: number
          picked_decoy: number
          total_decoy: number
          picked_decoy_items: { document_id: string; title: string; file_name: string }[]
          formula: string
        }
        total_evidence_score: number
        picked_unlabeled: { document_id: string; title: string; file_name: string }[]
        unpooled_uploads_count: number
        governance_notice: string
      },
      { case_id: string; team_id: string; submission_index?: 1 | 2 }
    >({
      // 2026-05-26: thêm submission_index (R1 dual flow) để AI tính riêng
      // chứng cứ HS1 vs HS2. BE accept qua query param ?submission_index=1|2.
      query: ({ case_id, team_id, submission_index }) => ({
        url: `/scoring/ai-evidence-classification/${case_id}/${team_id}`,
        method: 'POST',
        params: submission_index != null ? { submission_index } : undefined,
      }),
    }),
    // Rubric endpoints
    getRubrics: builder.query<Rubric[], { case_id?: string }>({
      query: (params) => ({
        url: '/scoring/rubrics',
        params,
      }),
      providesTags: ['Rubric'],
    }),
    getRubric: builder.query<Rubric, string>({
      query: (id) => `/scoring/rubrics/${id}`,
      providesTags: (result, error, id) => [{ type: 'Rubric', id }],
    }),
    createRubric: builder.mutation<Rubric, RubricCreate>({
      query: (rubric) => ({
        url: '/scoring/rubrics',
        method: 'POST',
        body: rubric,
      }),
      invalidatesTags: ['Rubric'],
    }),
    updateRubric: builder.mutation<Rubric, { id: string; update: Partial<RubricCreate> }>({
      query: ({ id, update }) => ({
        url: `/scoring/rubrics/${id}`,
        method: 'PUT',
        body: update,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Rubric', id }, 'Rubric'],
    }),
    deleteRubric: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/scoring/rubrics/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Rubric'],
    }),
    // 2026-05-26: Admin nhập phiếu chấm CK thay GK (offline paper form).
    createFinalProxyEntry: builder.mutation<
      Score,
      {
        case_id: string
        round_id: string
        team_id: string
        rubric_scores: Record<string, number>
        delegating_judge_id?: string
        proxy_judge_name?: string
        comments?: string
      }
    >({
      query: (body) => ({
        url: '/scoring/final-proxy-entry',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Score'],
    }),
  }),
})

export const {
  useGetScoresQuery,
  useGetScoreQuery,
  useCreateScoreMutation,
  useUpdateScoreMutation,
  useDeleteScoreMutation,
  useSubmitScoreMutation,
  useEnsureCourtroomScorecardMutation,
  useUnlockScoreMutation,
  useReopenScoreMutation,
  useLazyGetAISuggestionsQuery,
  useRunAiPreliminaryMemorialMutation,
  useRunAiEvidenceClassificationMutation,
  useFinalizeAiEvidenceScoreMutation,
  useGetRubricsQuery,
  useGetRubricQuery,
  useCreateRubricMutation,
  useUpdateRubricMutation,
  useDeleteRubricMutation,
  useGetScorecardProgressQuery,
  useProxyEnterScoreMutation,
  useCreateFinalProxyEntryMutation,
} = scoringApi
